import React, { useState, useEffect } from 'react'
import { HardDrive, Trash2, RefreshCw, AlertTriangle, CheckCircle, Info } from 'lucide-react'
import imageStorageService from '../services/imageStorageService'
import toast from 'react-hot-toast'

const StorageMonitor = ({ onClose }) => {
  const [storageUsage, setStorageUsage] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isCleaning, setIsCleaning] = useState(false)

  // 加载存储使用情况
  const loadStorageUsage = () => {
    setIsLoading(true)
    try {
      const usage = imageStorageService.getStorageUsage()
      setStorageUsage(usage)
    } catch (error) {
      console.error('获取存储使用情况失败:', error)
      toast.error('获取存储信息失败')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadStorageUsage()
  }, [])

  // 执行智能清理
  const handleSmartCleanup = async () => {
    if (!confirm('确定要执行智能清理吗？这将移除base64图片数据和不必要的缓存。')) {
      return
    }

    setIsCleaning(true)
    try {
      const result = imageStorageService.smartCleanup({
        removeBase64: true,
        removeLargeItems: true,
        removeOldData: true
      })

      toast.success(`清理完成！释放了 ${imageStorageService.formatSize(result.totalSizeFreed)} 空间`)
      
      // 刷新显示
      loadStorageUsage()
    } catch (error) {
      console.error('清理失败:', error)
      toast.error('清理失败: ' + error.message)
    } finally {
      setIsCleaning(false)
    }
  }

  // 获取状态颜色
  const getStatusColor = (percentage) => {
    if (percentage >= 90) return 'text-red-500'
    if (percentage >= 80) return 'text-orange-500'
    if (percentage >= 60) return 'text-yellow-500'
    return 'text-green-500'
  }

  // 获取状态图标
  const getStatusIcon = (percentage) => {
    if (percentage >= 80) return <AlertTriangle className="text-red-500" size={24} />
    return <CheckCircle className="text-green-500" size={24} />
  }

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
        <div className="bg-dark-800 rounded-lg p-6 max-w-2xl w-full mx-4">
          <div className="flex items-center justify-center">
            <RefreshCw className="animate-spin text-primary mr-2" size={24} />
            <span className="text-white">加载中...</span>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-dark-800 rounded-lg max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* 头部 */}
        <div className="flex items-center justify-between p-6 border-b border-dark-600">
          <div className="flex items-center space-x-3">
            <HardDrive className="text-primary" size={28} />
            <div>
              <h2 className="text-xl font-bold text-white">存储空间监控</h2>
              <p className="text-dark-300 text-sm">管理本地存储空间和优化数据</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-dark-400 hover:text-white transition-colors"
          >
            ✕
          </button>
        </div>

        {/* 内容区域 */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* 总体使用情况 */}
          <div className="bg-dark-700 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">总体使用情况</h3>
              {getStatusIcon(parseFloat(storageUsage?.percentage || 0))}
            </div>

            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-dark-300">已使用</span>
                  <span className={`font-mono font-semibold ${getStatusColor(parseFloat(storageUsage?.percentage || 0))}`}>
                    {storageUsage?.formattedTotalSize} / 5 MB ({storageUsage?.percentage}%)
                  </span>
                </div>
                <div className="w-full bg-dark-600 rounded-full h-4 overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${
                      parseFloat(storageUsage?.percentage || 0) >= 90
                        ? 'bg-red-500'
                        : parseFloat(storageUsage?.percentage || 0) >= 80
                        ? 'bg-orange-500'
                        : parseFloat(storageUsage?.percentage || 0) >= 60
                        ? 'bg-yellow-500'
                        : 'bg-green-500'
                    }`}
                    style={{ width: `${Math.min(storageUsage?.percentage || 0, 100)}%` }}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="bg-dark-600 rounded p-3">
                  <div className="text-dark-400 mb-1">存储项数量</div>
                  <div className="text-white font-semibold text-lg">{storageUsage?.itemCount || 0}</div>
                </div>
                <div className="bg-dark-600 rounded p-3">
                  <div className="text-dark-400 mb-1">可用空间</div>
                  <div className="text-white font-semibold text-lg">
                    {imageStorageService.formatSize((5 * 1024 * 1024) - (storageUsage?.totalSize || 0))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 存储建议 */}
          {parseFloat(storageUsage?.percentage || 0) >= 60 && (
            <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <Info className="text-orange-500 mt-0.5 flex-shrink-0" size={20} />
                <div className="flex-1">
                  <h4 className="text-orange-500 font-semibold mb-2">存储空间建议</h4>
                  <ul className="text-dark-200 text-sm space-y-1">
                    <li>• 建议清理不必要的故事或场景数据</li>
                    <li>• 图片应使用URL存储而非base64编码</li>
                    <li>• 定期执行智能清理以释放空间</li>
                    <li>• 考虑导出重要数据作为备份</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* 最大存储项 */}
          <div className="bg-dark-700 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-white mb-4">最大存储项（前10）</h3>
            <div className="space-y-2">
              {storageUsage?.items?.map((item, index) => (
                <div key={item.key} className="flex items-center justify-between p-3 bg-dark-600 rounded">
                  <div className="flex items-center space-x-3 flex-1 min-w-0">
                    <span className="text-dark-400 text-sm font-mono">#{index + 1}</span>
                    <span className="text-white truncate">{item.key}</span>
                  </div>
                  <span className="text-primary font-mono text-sm ml-3">{item.formattedSize}</span>
                </div>
              ))}
            </div>
          </div>

          {/* 清理说明 */}
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Info className="text-blue-400 mt-0.5 flex-shrink-0" size={20} />
              <div className="flex-1">
                <h4 className="text-blue-400 font-semibold mb-2">智能清理说明</h4>
                <div className="text-dark-200 text-sm space-y-1">
                  <p>智能清理将执行以下操作：</p>
                  <ul className="ml-4 mt-2 space-y-1">
                    <li>• 移除base64格式的图片数据（保留URL引用）</li>
                    <li>• 删除超过1MB的临时缓存数据</li>
                    <li>• 清理过期的用户编辑数据</li>
                    <li>• 保留核心故事和角色数据</li>
                  </ul>
                  <p className="mt-2 text-dark-400">注意：清理后页面可能需要重新加载图片</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 底部操作栏 */}
        <div className="border-t border-dark-600 p-6 flex justify-between items-center">
          <button
            onClick={loadStorageUsage}
            disabled={isLoading}
            className="flex items-center px-4 py-2 text-dark-300 hover:text-white transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`mr-2 ${isLoading ? 'animate-spin' : ''}`} size={18} />
            刷新
          </button>

          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-dark-600 text-white rounded-lg hover:bg-dark-500 transition-colors"
            >
              关闭
            </button>
            <button
              onClick={handleSmartCleanup}
              disabled={isCleaning}
              className="flex items-center px-6 py-2 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {isCleaning ? (
                <>
                  <RefreshCw className="animate-spin mr-2" size={18} />
                  清理中...
                </>
              ) : (
                <>
                  <Trash2 className="mr-2" size={18} />
                  智能清理
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default StorageMonitor


